import React, { useState } from "react";
import { FaShare } from "react-icons/fa";
import Swal from "sweetalert2";
import useShareRecipe from "../../hooks/useShareRecipe.js";
import useRealtimeShare from "../../hooks/useRealtimeShare.js";

/**
 * Bouton de partage pour la page visiteur
 * Permet de rendre une recette visible sur la page visiteur
 */
const VisitorShareButton = ({
  recipe,
  size = "default",
  className = "",
  onShareSuccess = () => {},
}) => {
  const [isSharing, setIsSharing] = useState(false);
  const [isShared, setIsShared] = useState(recipe?.is_shared || false);
  const { shareRecipe } = useShareRecipe();
  const { notifyShare, notifyUnshare } = useRealtimeShare();

  const handleShareToVisitorPage = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setIsSharing(true);

    try {
      console.log(
        `🔄 Tentative de partage de la recette ID: ${recipe.id} - "${recipe.title}"`,
      );

      const shareOptions = {
        showOnVisitorPage: !isShared,
        shareMessage: `🔥 ${recipe.title} – celle-là c’est du lourd, à tester !`,
        allowComments: true,
        showAuthorInfo: true,
      };

      console.log("📤 Options de partage:", shareOptions);
      const result = await shareRecipe(recipe.id, shareOptions);
      console.log("✅ Résultat du partage:", result);

      // Toggle l'état local
      setIsShared(!isShared);

      // Notification en temps réel
      if (!isShared) {
        notifyShare(recipe.id, recipe.title);
        Swal.fire({
          icon: "success",
          title: "En ligne !",
          text: `"${recipe.title}" – tout le monde peut kiffer !`,
          confirmButtonColor: "#22c55e",
          timer: 3000,
          timerProgressBar: true,
        });

        // Vérifier que le partage a bien fonctionné
        setTimeout(async () => {
          try {
            const response = await fetch(
              "http://localhost:3000/api/recipes/shared/public",
            );
            const data = await response.json();
            console.log(
              `✅ Vérification: ${
                data.data?.length || 0
              } recettes partagées au total`,
            );
          } catch (error) {
            console.error("❌ Erreur lors de la vérification:", error);
          }
        }, 2000);
      } else {
        notifyUnshare(recipe.id, recipe.title);
        Swal.fire({
          icon: "success",
          title: "Entre nous, swag.",
          text: `"${recipe.title}" retirée de la page.`,
          confirmButtonColor: "#8b5cf6",
          timer: 3000,
          timerProgressBar: true,
        });
      }

      // Callback de succès
      onShareSuccess();
    } catch (error) {
      console.error("Erreur lors du partage:", error);
      Swal.fire({
        icon: "error",
        title: "Oups !",
        text: "Erreur lors du partage de la recette.",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setIsSharing(false);
    }
  };

  const getButtonClasses = () => {
    const baseClasses =
      "flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-300 transform hover:scale-105";

    const sizeClasses = {
      small: "px-3 py-2 text-sm",
      default: "px-4 py-2",
      large: "px-6 py-3 text-lg",
    };

    const stateClasses = isShared
      ? "bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
      : "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white";

    return `${baseClasses} ${sizeClasses[size]} ${stateClasses} shadow-lg hover:shadow-xl ${className}`;
  };

  const getButtonContent = () => {
    if (isSharing) {
      return (
        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
      );
    }

    return (
      <FaShare
        className={size === "small" ? "w-4 h-4" : "w-5 h-5"}
        aria-hidden
      />
    );
  };

  const getTooltip = () => {
    return isShared
      ? "Elle est en ligne – clic pour la retirer de la page."
      : "Balance-la sur la page visiteur pour que tout le monde puisse la voir.";
  };

  return (
    <button
      type="button"
      onClick={(e) => handleShareToVisitorPage(e)}
      disabled={isSharing}
      className={getButtonClasses()}
      title={getTooltip()}
    >
      {getButtonContent()}
    </button>
  );
};

export default VisitorShareButton;
